import { ITableChangeState } from 'react-bootstrap-table-next';
import React from 'react';
import { ButtonDropdown, DataGrid, Icon, IconType } from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { IConversationData, IConversationOrderData, IConversationQuery } from '../../types/conversations/IConversation';
import OpenLinkInNewTab from 'policyUI/components/LinkInNewTab';
import { IConversationContactData } from '../../types/conversations/IConversationContact';
import { formatFromEnumToTitleCase } from '../../utilities/formatFromEnumToTitleCase';
import { ExternalUrlFactory } from '../../ui-toolkit/rest/ExternalUrlFactory';
import { DateFormat, getDateString } from '../../ui-toolkit/utilities/getDateString';

interface IConversationsDataGridProps {
  conversations: IPageData<IConversationData>;
  editingEnabled: boolean;
  query: IConversationQuery;
  setQuery: (query: IConversationQuery) => void;
  onDeleteClick: (conversationOrder: IConversationOrderData) => void;
  onEditClick: (conversationOrder: IConversationOrderData) => void;
}

const contactTypeString = (contact: IConversationContactData) => {
  const rawString = contact.purpose ? contact.purpose : contact.contactType;
  return rawString ? ` - ${formatFromEnumToTitleCase(rawString)}` : '';
};

export const ConversationsDataGrid: React.FC<IConversationsDataGridProps> = ({
  conversations,
  editingEnabled,
  query,
  setQuery,
  onDeleteClick,
  onEditClick,
}) => (
  <DataGrid
    data={conversations.content}
    columns={[
      {
        dataField: 'createdAt',
        formatter: (rawDate) =>
          typeof rawDate === 'string' ? getDateString(DateFormat.longDateTime, rawDate, true) : '',
        sort: false,
        text: 'Date',
      },
      {
        dataField: 'channelType',
        sort: false,
        text: 'Channel Type',
      },
      {
        dataField: 'contact',
        sort: false,
        text: 'Name',
        formatter: (value) => {
          if (value === null || typeof value === 'string') return '';
          return `${value.firstName} ${value.lastName}${contactTypeString(value)}`;
        },
      },
      {
        dataField: 'channelValue',
        sort: false,
        text: 'Contact Info',
      },
      {
        dataField: 'genesysId',
        formatter: (id) => (
          <OpenLinkInNewTab link={ExternalUrlFactory().urlFor.genesysCloud(`${id}`)}>
            Open in Genesys Cloud
          </OpenLinkInNewTab>
        ),
        sort: false,
        text: 'Details',
      },
      {
        dataField: 'id',
        formatter: (value, row) => (
          <ButtonDropdown
            disabled={!editingEnabled}
            intent="ghost"
            items={[
              {
                children: (
                  <>
                    <Icon type={IconType.Edit} /> Edit
                  </>
                ),
                onClick: () => {
                  onEditClick(row!);
                },
              },
              {
                children: (
                  <>
                    <Icon type={IconType.Remove} /> Delete
                  </>
                ),
                onClick: () => {
                  onDeleteClick(row! as IConversationOrderData);
                },
              },
            ]}
          >
            ...
          </ButtonDropdown>
        ),
        headerStyle: { width: 100 },
        text: 'Edit',
      },
    ]}
    keyField={'id'}
    onTableChange={(type: string, tableState: ITableChangeState<IConversationData>) => {
      setQuery({
        ...query,
        pageNumber: tableState.page,
        pageSize: tableState.sizePerPage,
      });
    }}
    pagination={{
      page: query.pageNumber || 1,
      sizePerPage: query.pageSize || 10,
      totalSize: conversations.totalElements,
    }}
  />
);
